import { milliCost, toMilli } from './fixed';
import { stockCities } from './stock';
import { recordExploration } from './systems/exploration';
import type { MatchConfig, SimContext, SimState } from './types';

/**
 * Mints a new unit id using the shared entity counter.
 * Increments state.nextEntityId after minting.
 */
function mintUnitId(state: SimState): string {
  const id = `u${state.nextEntityId}`;
  state.nextEntityId += 1;
  return id;
}

/**
 * Mints a new building id using the shared entity counter.
 * Increments state.nextEntityId after minting.
 */
function mintBuildingId(state: SimState): string {
  const id = `b${state.nextEntityId}`;
  state.nextEntityId += 1;
  return id;
}

/**
 * Deep-clones a SimState via JSON round-trip.
 * Serialization: JSON.stringify. Deserialization: JSON.parse. Both trivial.
 */
function cloneState(state: SimState): SimState {
  return JSON.parse(JSON.stringify(state)) as SimState;
}

interface PlayerSetup {
  playerId: string;
  cityPos: { q: number; r: number };
}

/**
 * Creates the initial SimState for a new game.
 * Players are processed in array order; id assignment contract:
 *   player 0 → b0, u1, u2; player 1 → b3, u4, u5 (etc.)
 * `match` (the lobby host's settings, see settings.ts `resolveMatch`) is
 * stored in the state; left out, the match runs on the rules' defaults.
 */
function createInitialState(
  ctx: SimContext,
  playerSetups: readonly PlayerSetup[],
  match?: MatchConfig
): SimState {
  const state: SimState = {
    tick: 0,
    elapsed: 0,
    status: 'running',
    players: {},
    units: {},
    buildings: {},
    tileResourceOverride: {},
    explored: {},
    nextEntityId: 0,
    ...(match ? { match: { ...match } } : {}),
  };

  for (const setup of playerSetups) {
    const { playerId, cityPos } = setup;

    state.players[playerId] = { eliminated: false };

    for (const buildingType of ctx.rules.player.startingBuildings) {
      const buildingDef = ctx.rules.buildings[buildingType];
      const id = mintBuildingId(state);
      state.buildings[id] = {
        id,
        owner: playerId,
        type: buildingType,
        pos: { q: cityPos.q, r: cityPos.r },
        hp: toMilli(buildingDef.hp),
        buildProgress: 1,
      };
    }
    // The starting resources go into the first city's stock.
    const firstCity = stockCities(state, ctx.rules, playerId)[0];
    if (firstCity) firstCity.stock = milliCost(ctx.rules.player.startingResources);

    for (const unitType of ctx.rules.player.startingUnits) {
      const unitDef = ctx.rules.units[unitType];
      const id = mintUnitId(state);
      state.units[id] = {
        id,
        owner: playerId,
        type: unitType,
        pos: { q: cityPos.q, r: cityPos.r },
        hp: toMilli(unitDef.hp),
        progress: 0,
        ...(firstCity ? { homeCityId: firstCity.id } : {}),
        ...(unitDef.automate ? { auto: {} } : {}),
      };
    }
  }

  // Players start having seen what their city and units see.
  recordExploration(state, ctx);
  return state;
}

export { cloneState, createInitialState, mintBuildingId, mintUnitId };
